import { useQuery } from "@tanstack/react-query";
import { useSessionStorage } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

import {
  fetchSessionMessages,
  TERMINAL_STATUSES,
} from "src/api/agents/copilot";
import { useAgentExecutionClient } from "src/api/agents/execution";
import { useModules } from "src/api/modulesApi";
import { useCurrentWorkspace } from "src/api/queries";
import {
  type Data,
  Environment,
  ExecutionMode,
  type SessionMessagesResponseMessagesInner,
} from "src/clients/agent-management";
import { ModuleKind } from "src/clients/flow-api";
import {
  LIVE_ANALYTICS_VIRTUAL_AGENT_ID,
  LIVE_ANALYTICS_VIRTUAL_VERSION_ID,
} from "src/flow/analytics/m1/liveAnalyticsVirtualAgent";
import { parseSuggestedQuestions } from "src/flow/analytics/m1/suggestedQuestions/parseSuggestedQuestions";
import { useLiveAnalyticsPreflight } from "src/flow/analytics/m1/useLiveAnalyticsPreflight";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useCurrentUserId } from "src/store/AuthStore";
import { getLocalTimezone } from "src/utils/timezones";

export const SUGGESTED_QUESTIONS_STORAGE_KEY =
  "liveAnalyticsSuggestedQuestionsV1";

export const SUGGESTED_QUESTIONS_MESSAGE =
  "Suggest 4 KPIs that matter for this decision flow given its schema. Phrase each as a question I could ask you. Reply with only a JSON array of strings, no prose, no chart, no query. Do not run any queries.";

const GENERATION_TIMEOUT_MS = 90_000;
const POLL_INTERVAL_MS = 1_000;

type AgentClient = NonNullable<
  ReturnType<typeof useAgentExecutionClient>["client"]
>;

type SuggestionsCache = Record<string, { questions: string[] }>;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const assistantText = (
  message: SessionMessagesResponseMessagesInner,
): string =>
  message.type === "ai"
    ? (message.content_blocks ?? [])
        .flatMap((block) => (block.type === "text" ? [block.text] : []))
        .join("\n\n")
    : "";

/** Polls the session until it reaches a terminal status and returns the last
 * assistant text, or `null` once the deadline passes. */
const awaitFinalAssistantText = async (
  client: AgentClient,
  sessionId: string,
  deadline: number,
): Promise<string | null> => {
  if (Date.now() >= deadline) return null;
  const page = await fetchSessionMessages({ client, sessionId });
  const status = page.session?.status;
  if (status !== undefined && TERMINAL_STATUSES.has(status)) {
    return (
      (page.messages ?? [])
        .map(assistantText)
        .find((text) => text.length > 0) ?? null
    );
  }
  await sleep(POLL_INTERVAL_MS);
  return awaitFinalAssistantText(client, sessionId, deadline);
};

export const useFlowSuggestedQuestions = ({
  flowId,
  flowName,
  enabled,
}: {
  flowId: string;
  flowName: string;
  enabled: boolean;
}): { questions: string[] | null; isGenerating: boolean } => {
  const userId = useCurrentUserId();
  const { data: workspace } = useCurrentWorkspace();
  const [environment] = useEnvironment();
  const { client } = useAgentExecutionClient();

  const [cache, setCache] = useSessionStorage<SuggestionsCache>(
    SUGGESTED_QUESTIONS_STORAGE_KEY,
    {},
  );
  const cacheKey = userId !== undefined ? `${userId}:${flowId}` : null;
  const cached =
    cacheKey !== null ? (cache[cacheKey]?.questions ?? null) : null;
  const wantsGeneration = enabled && cacheKey !== null && cached === null;

  const { structuredInput, flowVersionsResolved } = useLiveAnalyticsPreflight({
    flowId,
    flowName,
    enabled: wantsGeneration,
  });

  const modulesQuery = useModules({
    workspaceId: workspace?.id,
    kind: ModuleKind.ANALYTICS,
    enabled: wantsGeneration && Boolean(workspace?.id),
  });
  const demoMode =
    modulesQuery.data?.[0]?.spec?.config?.live_analytics_demo_mode === true;
  const demoModeResolved = modulesQuery.isSuccess || modulesQuery.isError;

  const generation = useQuery({
    queryKey: ["liveAnalyticsSuggestedQuestions", cacheKey],
    enabled:
      wantsGeneration &&
      client != null &&
      flowVersionsResolved &&
      demoModeResolved &&
      structuredInput !== null,
    staleTime: Infinity,
    queryFn: async (): Promise<string[] | null> => {
      if (client == null || cacheKey === null) return null;
      const sessionId = uuidv4();
      try {
        await client.invokeAgentCopilotsManagementApiV0InvokePost({
          data: {
            agent_id: LIVE_ANALYTICS_VIRTUAL_AGENT_ID,
            agent_version_id: LIVE_ANALYTICS_VIRTUAL_VERSION_ID,
            session_id: sessionId,
            invocation_message: SUGGESTED_QUESTIONS_MESSAGE,
            type: "invoke",
            source: "analytics",
            control: {
              execution_mode: ExecutionMode.ASYNC,
              environment:
                environment === "live"
                  ? Environment.PRODUCTION
                  : Environment.SANDBOX,
            },
            structured_input: structuredInput,
            client_timezone: getLocalTimezone(),
            inject_current_date: !demoMode,
          } as unknown as Data,
        });
        const text = await awaitFinalAssistantText(
          client,
          sessionId,
          Date.now() + GENERATION_TIMEOUT_MS,
        );
        const questions = text === null ? null : parseSuggestedQuestions(text);
        if (questions !== null)
          setCache((previous) => ({ ...previous, [cacheKey]: { questions } }));
        return questions;
      } catch {
        return null;
      }
    },
  });

  return {
    questions: enabled ? (cached ?? generation.data ?? null) : null,
    isGenerating: wantsGeneration && generation.isPending,
  };
};
